import humps from "humps";
import { Dict, EntityLocationType, ModelId } from "../../../../defines";
import { buildNameOfRecord } from "../../../../utils/accounts";
import { ApiModel, ApiModelOptions } from "../../../ApiModel";
import MallcopAccountEndpoints from "../../../endpoints/Account/mallcop";
import ManagerAccountEndpoints from "../../../endpoints/Account/manager";
import {
  AccountTypeType,
  TaxIdTypeEnumType,
  UsersAccountsPermissionLevelType,
} from "../../../enums";
import {
  AccountModel,
  CreateManagerAccountOutputModel,
  UsersAccountModel,
  VerificationModel,
} from "../../../models";
import { JsonApiManyResponse, ModelScope } from "../../../types";
import apiAddressModel from "../useAddressModel/apiAddressModel";

export default function apiAccountModel(opts: ApiModelOptions<AccountModel>) {
  const addressModel = apiAddressModel({
    apiClient: opts.apiClient,
    placementsApiClient: opts.placementsApiClient,
  });
  const model = ApiModel<AccountModel>("account", {
    urls: {
      [ModelScope.Mallcop]: MallcopAccountEndpoints,
      [ModelScope.Manager]: ManagerAccountEndpoints,
      ...opts.urls,
    },
    forcePlacement: {
      getMany: true,
      ...opts.forcePlacement,
    },
    ...opts,
  });

  const processAccountData = async (data: AccountModel, isJson?: boolean) => {
    if (
      data?.primaryAddress &&
      !data.primaryAddress.address1 &&
      !data.primaryAddress.id
    ) {
      data.primaryAddress = null;
    }

    if (
      data?.mailingAddress &&
      !data.mailingAddress.address1 &&
      !data.mailingAddress.id
    ) {
      data.mailingAddress = null;
    }

    const dataToSave = { ...data };

    if (!dataToSave.taxClassification) {
      dataToSave.taxClassification = null;
    }

    if (!dataToSave.corporationType) {
      dataToSave.corporationType = null;
    }

    if (!dataToSave.domicile) {
      dataToSave.domicile = null;
    }

    if (dataToSave.accountType === AccountTypeType.Trust) {
      dataToSave.entityRegisteredType = EntityLocationType.USRegistered;
      dataToSave.entityRegisteredCountry = "US";
    }

    if (dataToSave.accountType === AccountTypeType.Entity) {
      dataToSave.taxIdType = TaxIdTypeEnumType.Ein;

      if (dataToSave.entityRegisteredType === EntityLocationType.USRegistered) {
        dataToSave.entityRegisteredCountry = "US";
      }
    }

    if (dataToSave.accountType === AccountTypeType.Joint) {
      if (!dataToSave.nameOfRecord) {
        dataToSave.nameOfRecord = buildNameOfRecord(
          dataToSave,
          AccountTypeType.Joint,
        );
      }
    }

    if (dataToSave.accountType === AccountTypeType.Joint) {
      if (data.individual1?.id) {
        dataToSave.individual1 = { id: Number(data.individual1?.id) };
      }

      if (data.individual2?.id) {
        dataToSave.individual2 = { id: Number(data.individual2?.id) };
      }

      if (
        data.individual1?.users?.[0]?.id &&
        data.individual2?.users?.[0]?.id
      ) {
        dataToSave.usersAccounts = [
          {
            userId: Number(data.individual1?.users?.[0]?.id),
            permissionLevel: UsersAccountsPermissionLevelType.Owner,
          },
        ];

        if (
          data.individual1?.users?.[0]?.id !== data.individual2?.users?.[0]?.id
        ) {
          dataToSave.usersAccounts.push({
            userId: Number(data.individual2?.users?.[0]?.id),
            permissionLevel: UsersAccountsPermissionLevelType.Admin,
          });
        }
      }
    }

    if (
      dataToSave.accountType === AccountTypeType.Brokerage ||
      dataToSave.accountType === AccountTypeType.Retirement
    ) {
      if (data.custodiedOwner?.id) {
        dataToSave.custodiedOwner = {
          id:
            model.scope === ModelScope.Manager
              ? Number(data.custodiedOwner?.id)
              : data.custodiedOwner?.id,
        };
      }

      if (data.custodian?.id) {
        dataToSave.custodian = {
          id:
            model.scope === ModelScope.Manager
              ? Number(data.custodian?.id)
              : data.custodian?.id,
        };
      }

      if (data.custodiedOwner?.users?.[0]?.id) {
        dataToSave.usersAccounts = [
          {
            userId: Number(data.custodiedOwner?.users?.[0]?.id),
            permissionLevel: UsersAccountsPermissionLevelType.Owner,
          },
        ];
      }
    }

    if (!dataToSave.nameOfRecord && dataToSave.accountType) {
      dataToSave.nameOfRecord = buildNameOfRecord(
        dataToSave,
        dataToSave.accountType,
      );
    }

    if (
      dataToSave.primaryAddress &&
      dataToSave.primaryAddress.address1 &&
      !dataToSave.primaryAddress.id
    ) {
      const primaryAddress = await addressModel.create(
        dataToSave.primaryAddress,
      );
      dataToSave.primaryAddress = {
        id: isJson ? Number(primaryAddress?.id) : Number(primaryAddress?.id),
      };
    }

    // mailingAddressSame is not on model
    const mailingAddressSame = (data as any).mailingAddressSame;

    if (
      dataToSave.mailingAddress &&
      dataToSave.mailingAddress.address1 &&
      !dataToSave.mailingAddress.id
    ) {
      const mailingAddress = await addressModel.create(
        dataToSave.mailingAddress,
      );

      dataToSave.mailingAddress = {
        id: isJson ? Number(mailingAddress?.id) : Number(mailingAddress?.id),
      };
    } else if (dataToSave.primaryAddress?.id && mailingAddressSame) {
      dataToSave.mailingAddress = {
        id: isJson
          ? Number(dataToSave.primaryAddress.id)
          : Number(dataToSave.primaryAddress.id),
      };
    }

    return dataToSave;
  };

  const create = async (data: AccountModel, params?: any) => {
    const dataToSave = await processAccountData(data, true);

    return model.create(dataToSave, params);
  };

  const managerCreate = async (data: AccountModel, params?: any) => {
    const dataToSave = await processAccountData(data, true);

    const response = (await model.create(
      dataToSave,
      params,
      true,
    )) as CreateManagerAccountOutputModel;

    if (!response?.account?.id) throw new Error("Something went wrong");

    return response.account;
  };

  const update = async (
    id: ModelId,
    data: AccountModel,
    params?: any,
    preloadExisting = true,
  ) => {
    const dataToSave = await processAccountData(data);

    return model.update(id, dataToSave, params, preloadExisting);
  };

  const managerUpdate = async (
    id: ModelId,
    data: AccountModel,
    params?: Dict,
    preloadExisting = true,
  ) => {
    const dataToUpdate = await processAccountData(data, false);
    return model.update(id, dataToUpdate, params, preloadExisting, true);
  };

  const getVerificationsByAccountId = async (accountId: string | number) => {
    const verifications = await model.handler.apiRequest<
      JsonApiManyResponse<VerificationModel>
    >(`/verifications/account/${String(accountId)}`);

    return verifications.data || [];
  };

  const getAutoVerificationBaseUrl = (accountId: string | number) => {
    return `/accounts/kyc/${accountId}`;
  };

  const autoVerification = async (accountId: string | number) => {
    return await model.handler.apiRequest<
      JsonApiManyResponse<VerificationModel>
    >(getAutoVerificationBaseUrl(accountId));
  };

  const autoVerificationByManager = async (
    accountId: string | number,
    managerId: string | number,
  ) => {
    return await model.handler.apiRequest<
      JsonApiManyResponse<VerificationModel>
    >(`${getAutoVerificationBaseUrl(accountId)}?manager_id=${managerId}`);
  };

  const performKycCheck = async (accountId: string | number) => {
    return await model.handler.apiRequest(`/accounts/kyc/${accountId}`);
  };

  const performKycIndividual = async (accountId: string | number) => {
    return await model.handler.apiRequest(
      `/transacts/kyc/individual/${accountId}`,
    );
  };

  const createPartyIndividual = async (accountId: string | number) => {
    return await model.handler.apiRequest(`/transacts/individual/${accountId}`);
  };

  const performAmlEntity = async (accountId: string | number) => {
    return await model.handler.apiRequest(`transacts/aml/entity/${accountId}`);
  };

  const createTransactApiEntity = async (accountId: string | number) => {
    return await model.handler.apiRequest(`transacts/entity/${accountId}`);
  };

  const getRelatedAccounts = async (accountId: string | number) => {
    const result = await model.handler.apiRequest<
      JsonApiManyResponse<AccountModel>
    >(`/accounts/${accountId}/related`);

    return result.data || [];
  };

  const shareAccountToUserId = async (
    accountId: string | number,
    userId: string | number,
    permission: UsersAccountsPermissionLevelType,
  ) => {
    return await model.placementsApiClient.rpcRequest("share-account", {
      account_id: Number(accountId),
      user_id: Number(userId),
      permission,
    });
  };

  const unshareAccountToUserId = async (
    accountId: string | number,
    userId: string | number,
  ) => {
    return await model.placementsApiClient.rpcRequest("unshare-account", {
      account_id: Number(accountId),
      user_id: Number(userId),
    });
  };

  const mergeAccounts = async (
    fromAccountId: string | number,
    toAccountId: string | number,
  ) => {
    return await model.handler.rpcRequest("merge-accounts", {
      id: Number(toAccountId),
      other_id: Number(fromAccountId),
    });
  };

  const updateUsersAccounts = async (
    accountId: ModelId,
    userAccounts: UsersAccountModel[],
  ) => {
    return await model.handler.post(
      `/manager/accounts/${accountId}/users`,
      humps.decamelizeKeys(userAccounts),
      false,
    );
  };

  return {
    ...model,
    performKycCheck,
    getVerificationsByAccountId,
    performKycIndividual,
    createPartyIndividual,
    performAmlEntity,
    autoVerification,
    autoVerificationByManager,
    createTransactApiEntity,
    getRelatedAccounts,
    shareAccountToUserId,
    unshareAccountToUserId,
    mergeAccounts,
    create,
    managerCreate,
    update,
    managerUpdate,
    updateUsersAccounts,
  };
}
